import React, { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Buttons from "../../../../components/UI/Buttons";
import FormInputs from "../../../../components/UI/FormInputs";
import DropDownService from "../../../../services/DropDownService";
import MedicationService from "../../../../services/MedicationService";
import { isSet } from "../../../../utils/commonUtils";
import TableGrid from "../../../../components/UI/TableGrid";
import PERMISSIONS from "../../../../utils/AclPermissions";

const AddPrescriptionModal = (props) => {
  const { isOpen, onClose, onSubmit, Toast, visitDetails, visit_id, Loader, initialData, isEditMode = false } = props;

  const [providerOptions, setProviderOptions] = useState([{ label: "Select Provider", value: "" }]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [refill, setRefill] = useState("");
  const [prescriptionReference, setPrescriptionReference] = useState("");
  
  // Medication selection fields
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [selectedMedication, setSelectedMedication] = useState("");
  const [sig, setSig] = useState("");
  const [quantity, setQuantity] = useState("");
  const [medicationForm, setMedicationForm] = useState("");
  const [prescribedDays, setPrescribedDays] = useState("");
  
  // Added medications list
  const [medicationsList, setMedicationsList] = useState([]);
  
  // Track prescription_id for updates
  const [prescriptionId, setPrescriptionId] = useState(null);
  
  // Track which medication is being edited (using medication_id + index as unique identifier)
  const [editingMedicationId, setEditingMedicationId] = useState(null);
  // Track the original medication ID that was clicked for edit (to find it in the list for update)
  const [originalEditingMedicationId, setOriginalEditingMedicationId] = useState(null);
  const isEditingRef = useRef(false);
  
  // Validation errors for medication fields
  const [medicationValidation, setMedicationValidation] = useState({
    category: "",
    medication_name: "",
    quantity: "",
    medication_form: "",
    prescribed_days: "",
    sig: "",
  });
  
  // Table headers for added medications
  const medicationTableHeaders = [
    { label: "Medication Name", key: "medication_name" },
    { label: "Category", key: "category" },
    { label: "Quantity", key: "quantity" },
    // { label: "Form", key: "medication_form" },
    { label: "Days", key: "prescribed_days" },
    { label: "SIG", key: "sig" },
    { label: "Action", key: "action", type: "Actions" },
  ];

  // Table records state for added medications
  const [medicationTableRecords, setMedicationTableRecords] = useState({
    tableHeaders: medicationTableHeaders,
    tableRows: {
      data: [],
      status: 200,
    },
  });

  // Fetch providers and populate form if initialData is provided
  useEffect(() => {
    if (isOpen) {
      fetchProviders();
      fetchCategories();
      
      // If initialData is provided (view/edit mode), populate the form
      if (initialData) {
        setSelectedProvider(initialData.provider_id || "");
        setRefill(initialData.refill?.toString() || "");
        setPrescriptionReference(initialData.prescription_reference || "");
        setPrescriptionId(initialData.prescription_id || null); // Store prescription_id for updates
        
        // Populate medications list
        if (initialData.medications && initialData.medications.length > 0) {
          const formattedMedications = initialData.medications.map(med => ({
            medication_id: med.medication_id,
            medication_name: med.medication_name,
            category: med.category,
            quantity: med.quantity?.toString() || "",
            medication_form: med.medication_form || "",
            prescribed_days: med.prescribed_days?.toString() || "",
            sig: med.sig || "",
          }));
          setMedicationsList(formattedMedications);
          
          // Update table records
          const tableData = formattedMedications.map((med, index) => ({
            rowId: `med_${index}_${med.medication_id}`,
            data: {
              medication_name: {
                value: (
                  <span className="forTable_textEllipsis" title={med.medication_name || "-"}>
                    {med.medication_name || "-"}
                  </span>
                ),
              },
              category: {
                value: (
                  <span className="forTable_textEllipsis" title={med.category || "-"}>
                    {med.category || "-"}
                  </span>
                ),
              },
              quantity: {
                value: (
                  <span className="forTable_textEllipsis" title={`${med.quantity || ""} ${med.medication_form || ""}`.trim() || "-"}>
                    {`${med.quantity || ""} ${med.medication_form || ""}`.trim() || "-"}
                  </span>
                ),
              },
              prescribed_days: {
                value: (
                  <span className="forTable_textEllipsis" title={med.prescribed_days || "-"}>
                    {med.prescribed_days || "-"}
                  </span>
                ),
              },
              sig: {
                value: (
                  <span className="forTable_textEllipsis" title={med.sig || "-"}>
                    {med.sig || "-"}
                  </span>
                ),
              },
              action: [
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Edit",
                  title: "Edit",
                  acl: PERMISSIONS.VISITS.EDIT,
                  clickHandler: (e) => {
                    e?.stopPropagation?.();
                    e?.preventDefault?.();
                    handleEditMedication(med);
                  },
                },
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Remove",
                  title: "Remove",
                  isDelete: true,
                  acl: PERMISSIONS.VISITS.DELETE,
                  clickHandler: (e) => {
                    e?.stopPropagation?.();
                    e?.preventDefault?.();
                    // Pass the medication object directly to avoid index issues
                    handleRemoveMedication(med);
                  },
                },
              ],
            },
          }));
          
          setMedicationTableRecords({
            tableHeaders: medicationTableHeaders,
            tableRows: {
              data: tableData,
              status: 200,
            },
          });
        }
      } else {
        // Add mode: set defaults for new prescription
        setRefill("0");
        setPrescriptionReference("prescription");
        // Auto-select primary provider if available (add mode)
        if (visitDetails?.["0"]?.primary_provider_id || visitDetails?.primary_provider_id) {
          setSelectedProvider(visitDetails?.["0"]?.primary_provider_id || visitDetails?.primary_provider_id);
        }
      }
    } else {
      // Reset fields when modal closes
      setSelectedProvider("");
      setRefill("");
      setPrescriptionReference("");
      setPrescriptionId(null); // Reset prescription_id when modal closes
      setMedicationsList([]);
      setSelectedCategory("");
      setSelectedMedication("");
      setSig("");
      setQuantity("");
      setMedicationForm("");
      setPrescribedDays("");
      setEditingMedicationId(null);
      setOriginalEditingMedicationId(null);
      isEditingRef.current = false; // Reset ref flag when modal closes
      setMedicationTableRecords({
        tableHeaders: medicationTableHeaders,
        tableRows: {
          data: [],
          status: 200,
        },
      });
    }
  }, [isOpen, visitDetails, initialData, isEditMode]);

  // Fetch medications when category changes (but not when editing)
  useEffect(() => {
    if (selectedCategory && isOpen && !isEditingRef.current) {
      fetchMedicationsByCategory(selectedCategory);
    } else if (!selectedCategory && !isEditingRef.current) {
      setMedicationOptions([]);
      setSelectedMedication("");
    }
  }, [selectedCategory, isOpen]);

      // Auto-populate fields when medication is selected
  useEffect(() => {
    // Auto-populate if medication is selected and options are available
    if (selectedMedication && medicationOptions.length > 0) {
      const medication = medicationOptions.find(med => med.value === selectedMedication);
      if (medication && medication.data) {
        // Check if this is a manual change (user changed the dropdown)
        // We track this by checking if the selected medication differs from the one being edited
        const isManualChange = editingMedicationId && selectedMedication !== editingMedicationId;
        
        // Auto-populate if:
        // 1. Not in edit mode, OR
        // 2. In edit mode but medication was manually changed (different from the one being edited)
        if (!editingMedicationId || isManualChange) {
          setSig(medication.data.sig || "");
          setQuantity(medication.data.quantity || "");
          setMedicationForm(medication.data.medication_form || "");
          // Note: prescribedDays is not auto-populated as it's specific to each prescription
          
          // Clear validation errors when fields are auto-populated
          setMedicationValidation(prev => ({
            ...prev,
            medication_name: "",
            sig: "",
            quantity: "",
            medication_form: "",
          }));
          
          // If medication was manually changed during edit, update the editingMedicationId to the new one
          // This keeps the button as "Update" but allows auto-populate
          if (isManualChange) {
            setEditingMedicationId(selectedMedication);
          }
        }
      }
    }
  }, [selectedMedication, medicationOptions, editingMedicationId]);

  const fetchProviders = async () => {
    try {
      const providers = await DropDownService.getProvidersDropdownList(null);
      if (providers && providers.length > 0) {
        const options = [{ label: "Select Provider", value: "" }];
        providers.forEach((provider) => {
          options.push({
            label: `${provider.first_name} ${provider.last_name}`,
            value: provider.provider_id,
          });
        });
        setProviderOptions(options);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      if (Loader) Loader(true);
      const response = await MedicationService.getAllMedications(null, {});
      if (response && response.data) {
        // Extract unique categories and sort in ascending order
        const categories = [...new Set(response.data.map(med => med.category).filter(Boolean))].sort();
        const categoryOptions = [{ label: "Select Category", value: "" }, ...categories.map(cat => ({ label: cat, value: cat }))];
        setCategoryOptions(categoryOptions);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      if (Toast) Toast.error("Failed to fetch categories");
    } finally {
      if (Loader) Loader(false);
    }
  };

  const fetchMedicationsByCategory = async (category) => {
    try {
      const response = await MedicationService.getAllMedications(null, { 
        search_query: "",
      });
      if (response && response.data) {
        // Filter medications by category (category is encrypted, so we need to decrypt)
        const filtered = response.data.filter(med => med.category === category);
        const options = [{ label: "Select Medication", value: "" }, ...filtered.map(med => ({
          label: med.medication_name,
          value: med.medication_id,
          data: med
        }))];
        setMedicationOptions(options);
        return options;
      }
      return [];
    } catch (error) {
      console.error("Error fetching medications:", error);
      if (Toast) Toast.error("Failed to fetch medications");
      return [];
    }
  };

  const handleAddMedication = () => {
    // Reset validation
    const newValidation = {
      category: "",
      medication_name: "",
      quantity: "",
      medication_form: "",
      prescribed_days: "",
      sig: "",
    };
    
    let hasError = false;
    
    if (!selectedCategory) {
      newValidation.category = "Category is required";
      hasError = true;
    }
    if (!selectedMedication) {
      newValidation.medication_name = "Medication Name is required";
      hasError = true;
    }
    if (!quantity) {
      newValidation.quantity = "Quantity is required";
      hasError = true;
    }
    if (!medicationForm) {
      newValidation.medication_form = "Medication Form is required";
      hasError = true;
    }
    if (!prescribedDays) {
      newValidation.prescribed_days = "Prescribed for how many days is required";
      hasError = true;
    }
    if (!sig || sig.trim() === "") {
      newValidation.sig = "SIG is required";
      hasError = true;
    }
    
    if (hasError) {
      setMedicationValidation(newValidation);
      return;
    }
    
    // Clear validation if all fields are valid
    setMedicationValidation({
      category: "",
      medication_name: "",
      quantity: "",
      medication_form: "",
      prescribed_days: "",
      sig: "",
    });

    const medication = medicationOptions.find(med => med.value === selectedMedication);
    if (medication && medication.data) {
      const newMedication = {
        medication_id: selectedMedication,
        medication_name: medication.data.medication_name,
        generic_name: medication.data.generic_name,
        category: selectedCategory,
        quantity: quantity,
        medication_form: medicationForm,
        sig: sig,
        prescribed_days: prescribedDays,
      };
      
      let updatedList;
      if (originalEditingMedicationId !== null) {
        // Update existing medication - find by original medication_id (the one that was clicked for edit)
        updatedList = medicationsList.map(med => 
          med.medication_id === originalEditingMedicationId ? newMedication : med
        );
        setEditingMedicationId(null); // Clear editing state
        setOriginalEditingMedicationId(null); // Clear original editing medication ID
      } else {
        // Add new medication
        updatedList = [...medicationsList, newMedication];
      }
      setMedicationsList(updatedList);
      
      // Update table records
      const tableData = updatedList.map((med, index) => ({
        rowId: `med_${index}_${med.medication_id}`,
        data: {
          medication_name: {
            value: (
              <span className="forTable_textEllipsis" title={med.medication_name || "-"}>
                {med.medication_name || "-"}
              </span>
            ),
          },
          category: {
            value: (
              <span className="forTable_textEllipsis" title={med.category || "-"}>
                {med.category || "-"}
              </span>
            ),
          },
          quantity: {
            value: (
              <span className="forTable_textEllipsis" title={`${med.quantity || ""} ${med.medication_form || ""}`.trim() || "-"}>
                {`${med.quantity || ""} ${med.medication_form || ""}`.trim() || "-"}
              </span>
            ),
          },
        //   medication_form: {
        //     value: (
        //       <span className="forTable_textEllipsis" title={med.medication_form || "-"}>
        //         {med.medication_form || "-"}
        //       </span>
        //     ),
        //   },
          prescribed_days: {
            value: (
              <span className="forTable_textEllipsis" title={med.prescribed_days || "-"}>
                {med.prescribed_days || "-"}
              </span>
            ),
          },
          sig: {
            value: (
              <span className="forTable_textEllipsis" title={med.sig || "-"}>
                {med.sig || "-"}
              </span>
            ),
          },
              action: [
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Edit",
                  title: "Edit",
                  acl: PERMISSIONS.VISITS.EDIT,
                  clickHandler: (e) => {
                    e?.stopPropagation?.();
                    e?.preventDefault?.();
                    handleEditMedication(med);
                  },
                },
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Remove",
                  title: "Remove",
                  isDelete: true,
                  acl: PERMISSIONS.VISITS.DELETE,
                  clickHandler: (e) => {
                    e?.stopPropagation?.();
                    e?.preventDefault?.();
                    // Pass the medication object directly to avoid index issues
                    handleRemoveMedication(med);
                  },
                },
              ],
        },
      }));
      
      setMedicationTableRecords({
        tableHeaders: medicationTableHeaders,
        tableRows: {
          data: tableData,
          status: 200,
        },
      });
      
      // Reset medication selection fields
      setSelectedCategory("");
      setSelectedMedication("");
      setSig("");
      setQuantity("");
      setMedicationForm("");
      setPrescribedDays("");
      setEditingMedicationId(null); // Clear editing state
      setOriginalEditingMedicationId(null); // Clear original editing medication ID
      isEditingRef.current = false; // Reset ref flag
      
      // Clear validation errors
      setMedicationValidation({
        category: "",
        medication_name: "",
        quantity: "",
        medication_form: "",
        prescribed_days: "",
        sig: "",
      });
    }
  };
  
  // Clear validation error when field changes
  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);
    if (medicationValidation[fieldName]) {
      setMedicationValidation(prev => ({
        ...prev,
        [fieldName]: ""
      }));
    }
    
    // If medication name is changed manually during edit, update editingMedicationId to the new value
    // This keeps the button as "Update" while allowing auto-populate
    if (fieldName === "medication_name" && editingMedicationId && value !== editingMedicationId) {
      // The useEffect for auto-populate will handle updating editingMedicationId
      // We just need to ensure the ref doesn't block auto-populate
      isEditingRef.current = false;
    }
  };

  const handleEditMedication = async (medicationOrId, index) => {
    // Support both: receiving medication object directly or medicationId + index
    let medication;
    if (typeof medicationOrId === 'object' && medicationOrId !== null) {
      // Medication object passed directly
      medication = medicationOrId;
    } else {
      // medicationId passed, need to find it in the list
      const medicationId = medicationOrId;
      medication = medicationsList.find(med => med.medication_id === medicationId) || medicationsList[index];
      if (!medication) {
        console.error("Medication not found for editing", { medicationId, index, medicationsList });
        if (Toast) Toast.error("Medication not found for editing");
        return;
      }
    }
    
    console.log("handleEditMedication called", { medication, medicationsList });
    console.log("Editing medication:", medication);
    
    // Set ref flag first to prevent useEffect interference
    isEditingRef.current = true;
    
    // Store the original medication_id that we're editing (for tracking which one to update in the list)
    setOriginalEditingMedicationId(medication.medication_id);
    
    // Set editing medication ID (this will track the current selection, may change if user changes dropdown)
    setEditingMedicationId(medication.medication_id);
    
    // Populate quantity, form, days, and sig immediately (don't depend on async operations)
    setQuantity(medication.quantity?.toString() || "");
    setMedicationForm(medication.medication_form || "");
    setPrescribedDays(medication.prescribed_days?.toString() || "");
    setSig(medication.sig || "");
    
    // Clear validation errors when editing
    setMedicationValidation({
      category: "",
      medication_name: "",
      quantity: "",
      medication_form: "",
      prescribed_days: "",
      sig: "",
    });
    
    // Fetch medications for the selected category if category is set
    if (medication.category) {
      try {
        // Fetch medications first
        const options = await fetchMedicationsByCategory(medication.category);
        console.log("Fetched medication options:", options?.length || 0);
        
        // Wait a bit for state to update with options
        setTimeout(() => {
          // Set category and medication after options are loaded
          setSelectedCategory(medication.category || "");
          
          // Small delay to ensure category state is updated and options are available
          setTimeout(() => {
            setSelectedMedication(medication.medication_id || "");
            
            // Reset the ref flag after a delay to allow auto-populate on manual changes
            setTimeout(() => {
              isEditingRef.current = false;
            }, 300);
            
            console.log("Populated edit fields:", {
              category: medication.category,
              medication_id: medication.medication_id,
              quantity: medication.quantity,
              form: medication.medication_form,
              days: medication.prescribed_days,
              sig: medication.sig,
              optionsCount: options?.length || 0
            });
          }, 150);
        }, 100);
      } catch (error) {
        console.error("Error fetching medications for edit:", error);
        // Still populate category and medication_id even if fetch fails
        setSelectedCategory(medication.category || "");
        setSelectedMedication(medication.medication_id || "");
        isEditingRef.current = false;
      }
    } else {
      // If no category, just populate fields
      setSelectedCategory("");
      setSelectedMedication(medication.medication_id || "");
      isEditingRef.current = false;
    }
    
    // Scroll to the "Add Medication" section
    setTimeout(() => {
      const addMedicationCards = document.querySelectorAll('.card.mb-3');
      if (addMedicationCards && addMedicationCards.length > 0) {
        // Find the "Add Medication" card (usually the first one with form fields)
        const addMedicationCard = Array.from(addMedicationCards).find(card => 
          card.querySelector('h6')?.textContent?.includes('Add Medication')
        );
        if (addMedicationCard) {
          addMedicationCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 400);
  };

  const handleRemoveMedication = (medicationOrIndex) => {
    // Support both: receiving medication object directly or index
    let medicationToRemove;
    let medicationId;
    
    if (typeof medicationOrIndex === 'object' && medicationOrIndex !== null) {
      // Medication object passed directly
      medicationToRemove = medicationOrIndex;
      medicationId = medicationToRemove.medication_id;
    } else {
      // Index passed, find medication by index using functional update
      const index = medicationOrIndex;
      setMedicationsList(currentList => {
        medicationToRemove = currentList[index];
        if (!medicationToRemove) {
          console.error("Medication not found for removal", { index, currentList });
          return currentList;
        }
        medicationId = medicationToRemove.medication_id;
        return currentList.filter(med => med.medication_id !== medicationId);
      });
      
      if (!medicationId) {
        return; // Medication not found
      }
    }
    
    console.log("handleRemoveMedication", { medicationToRemove, medicationId });
    
    // If removing the medication being edited, clear editing state
    if (originalEditingMedicationId === medicationId || editingMedicationId === medicationId) {
      setEditingMedicationId(null);
      setOriginalEditingMedicationId(null);
      isEditingRef.current = false;
      setSelectedCategory("");
      setSelectedMedication("");
      setSig("");
      setQuantity("");
      setMedicationForm("");
      setPrescribedDays("");
    }
    
    // Remove medication by medication_id using functional update to ensure we use latest state
    if (medicationId) {
      setMedicationsList(currentList => {
        const updatedList = currentList.filter(med => med.medication_id !== medicationId);
        
        // Update table records with the updated list
        const tableData = updatedList.map((med, idx) => ({
          rowId: `med_${idx}_${med.medication_id}`,
          data: {
            medication_name: {
              value: (
                <span className="forTable_textEllipsis" title={med.medication_name || "-"}>
                  {med.medication_name || "-"}
                </span>
              ),
            },
            category: {
              value: (
                <span className="forTable_textEllipsis" title={med.category || "-"}>
                  {med.category || "-"}
                </span>
              ),
            },
            quantity: {
              value: (
                <span className="forTable_textEllipsis" title={`${med.quantity || ""} ${med.medication_form || ""}`.trim() || "-"}>
                  {`${med.quantity || ""} ${med.medication_form || ""}`.trim() || "-"}
                </span>
              ),
            },
            prescribed_days: {
              value: (
                <span className="forTable_textEllipsis" title={med.prescribed_days || "-"}>
                  {med.prescribed_days || "-"}
                </span>
              ),
            },
            sig: {
              value: (
                <span className="forTable_textEllipsis" title={med.sig || "-"}>
                  {med.sig || "-"}
                </span>
              ),
            },
            action: [
              {
                className: "btn btn-icon btn-transparent-dark",
                iconType: "Edit",
                title: "Edit",
                acl: PERMISSIONS.VISITS.EDIT,
                clickHandler: (e) => {
                  e?.stopPropagation?.();
                  e?.preventDefault?.();
                  handleEditMedication(med);
                },
              },
              {
                className: "btn btn-icon btn-transparent-dark",
                iconType: "Remove",
                title: "Remove",
                isDelete: true,
                acl: PERMISSIONS.VISITS.DELETE,
                clickHandler: (e) => {
                  e?.stopPropagation?.();
                  e?.preventDefault?.();
                  // Pass the medication object directly to avoid index issues
                  handleRemoveMedication(med);
                },
              },
            ],
          },
        }));
        
        setMedicationTableRecords({
          tableHeaders: medicationTableHeaders,
          tableRows: {
            data: tableData,
            status: 200,
          },
        });
        
        return updatedList;
      });
    }
  };

  const handleSave = () => {
    if (!selectedProvider) {
      if (Toast) Toast.error("Please select a provider");
      return;
    }
    if (medicationsList.length === 0) {
      if (Toast) Toast.error("Please add at least one medication");
      return;
    }
    if (!refill || refill === "") {
      if (Toast) Toast.error("Refill is required");
      return;
    }
    if (!prescriptionReference || prescriptionReference.trim() === "") {
      if (Toast) Toast.error("Prescription Reference is required");
      return;
    }

    const prescriptionData = {
      provider_id: selectedProvider,
      refill: parseInt(refill),
      prescription_reference: prescriptionReference.trim(),
      medications: medicationsList.map(med => ({
        medication_id: med.medication_id,
        quantity: parseInt(med.quantity),
        medication_form: med.medication_form,
        prescribed_days: parseInt(med.prescribed_days),
        sig: med.sig || null,
      })),
    };

    // Include prescription_id if it exists (for updates)
    if (prescriptionId) {
      prescriptionData.prescription_id = prescriptionId;
    }

    console.log("Saving prescription data:", prescriptionData);

    if (onSubmit) {
      onSubmit(prescriptionData);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="xl"
      dialogClassName="hResponsive"
      backdrop="static"
      keyboard={false}
      contentClassName="h-100"
    >
      <div className="sh_modalBox">
        <Modal.Header closeButton>
          <Modal.Title>
            <h6 className="text-lg mb-0 font-weight-bold">{isEditMode ? "Edit Prescription" : "Add Prescription"}</h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="form-group">
                <label className="mb-1 text-sm opacity-75">
                  Provider <span className="text-danger">*</span>
                </label>
                <FormInputs
                  type="SelectList"
                  name="provider"
                  className="form-control form-control-solid"
                  options={providerOptions}
                  value={selectedProvider}
                  changeHandler={(val) => setSelectedProvider(val)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label className="mb-1 text-sm opacity-75">
                  Refill <span className="text-danger">*</span>
                </label>
                <FormInputs
                  type="Number"
                  name="refill"
                  className="form-control form-control-solid"
                  value={refill}
                  changeHandler={(val) => setRefill(val)}
                  placeholder="Enter refill"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label className="mb-1 text-sm opacity-75">
                  Prescription Reference <span className="text-danger">*</span>
                </label>
                <FormInputs
                  type="TextInput"
                  name="prescription_reference"
                  className="form-control form-control-solid"
                  value={prescriptionReference}
                  changeHandler={(val) => setPrescriptionReference(val)}
                  placeholder="Enter prescription reference"
                />
              </div>
            </div>
          </div>

          {/* Medication Selection and List in Single Row */}
          <div className="row">
            {/* Add Medication - col-4 */}
            <div className="col-md-4">
              <div className="card mb-3" style={{ border: "1px solid #e0e0e0" }}>
                <div className="card-header d-flex justify-content-between align-items-center p-2">
                  <h6 className="mb-0 text-md">Add Medication</h6>
                </div>
                <div className="card-body">
                  <div className="row ">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label className="mb-1 text-sm opacity-75">
                          Category <span className="text-danger">*</span>
                        </label>
                        <FormInputs
                          type="SelectList"
                          name="category"
                          className="form-control form-control-solid"
                          options={categoryOptions}
                          value={selectedCategory}
                          changeHandler={(val) => handleFieldChange("category", val, setSelectedCategory)}
                        />
                        {medicationValidation.category && (
                          <span className="text-danger small d-block mt-1">{medicationValidation.category}</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label className="mb-1 text-sm opacity-75">
                          Medication Name <span className="text-danger">*</span>
                        </label>
                        <FormInputs
                          type="SelectList"
                          name="medication_name"
                          className="form-control form-control-solid"
                          options={medicationOptions}
                          value={selectedMedication}
                          changeHandler={(val) => handleFieldChange("medication_name", val, setSelectedMedication)}
                        />
                        {medicationValidation.medication_name && (
                          <span className="text-danger small d-block mt-1">{medicationValidation.medication_name}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row ">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label className="mb-1 text-sm opacity-75">
                          Quantity <span className="text-danger">*</span>
                        </label>
                        <FormInputs
                          type="Number"
                          name="quantity"
                          className="form-control form-control-solid"
                          value={quantity}
                          changeHandler={(val) => handleFieldChange("quantity", val, setQuantity)}
                          placeholder="Enter quantity"
                        />
                        {medicationValidation.quantity && (
                          <span className="text-danger small d-block mt-1">{medicationValidation.quantity}</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label className="mb-1 text-sm opacity-75">
                          Medication Form <span className="text-danger">*</span>
                        </label>
                        <FormInputs
                          type="TextInput"
                          name="medication_form"
                          className="form-control form-control-solid"
                          value={medicationForm}
                          changeHandler={(val) => handleFieldChange("medication_form", val, setMedicationForm)}
                          placeholder="e.g., Tablet, Capsule"
                        />
                        {medicationValidation.medication_form && (
                          <span className="text-danger small d-block mt-1">{medicationValidation.medication_form}</span>
                        )}
                      </div>
                    </div>
                    {/* <div className="col-md-12">
                      <div className="form-group">
                        <label className="mb-1 text-sm opacity-75">
                          Prescribed for how many days <span className="text-danger">*</span>
                        </label>
                        <FormInputs
                          type="Number"
                          name="prescribed_days"
                          className="form-control form-control-solid"
                          value={prescribedDays}
                          changeHandler={(val) => handleFieldChange("prescribed_days", val, setPrescribedDays)}
                          placeholder="Enter days"
                        />
                        {medicationValidation.prescribed_days && (
                          <span className="text-danger small d-block mt-1">{medicationValidation.prescribed_days}</span>
                        )}
                      </div>
                    </div> */}
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label className="mb-1 text-sm opacity-75">
                          SIG <span className="text-danger">*</span>
                        </label>
                        <FormInputs
                          type="TextArea"
                          name="sig"
                          className="form-control form-control-solid"
                          value={sig}
                          changeHandler={(val) => handleFieldChange("sig", val, setSig)}
                          placeholder="Enter SIG"
                        />
                        {medicationValidation.sig && (
                          <span className="text-danger small d-block mt-1">{medicationValidation.sig}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                    <div className="form-group">
                        <label className="mb-1 text-sm opacity-75">
                          Prescribed for how many days <span className="text-danger">*</span>
                        </label>
                        <FormInputs
                          type="Number"
                          name="prescribed_days"
                          className="form-control form-control-solid"
                          value={prescribedDays}
                          changeHandler={(val) => handleFieldChange("prescribed_days", val, setPrescribedDays)}
                          placeholder="Enter days"
                        />
                        {medicationValidation.prescribed_days && (
                          <span className="text-danger small d-block mt-1">{medicationValidation.prescribed_days}</span>
                        )}
                      </div>
                    </div>
                  </div>
  
                  <div className="row mt-3">
                    <div className="col-12 d-flex justify-content-center">
                      <Buttons
                        className="btn btn-primary"
                        label={editingMedicationId !== null ? "Update Prescription" : "Add Prescription"}
                        clickHandler={handleAddMedication}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Added Medications - col-8 */}
            <div className="col-md-8">
              <div className="card mb-3" style={{ border: "1px solid #e0e0e0" }}>
                <div className="card-header">
                  <h6 className="mb-0 text-md">Added Medications</h6>
                </div>
                <div className="card-body p-0">
                  <div style={{ maxHeight: "200px", overflowY: "auto", overflowX: "auto" }}>
                    <TableGrid
                      hasPagination={false}
                      {...medicationTableRecords}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Buttons
            className="btn btn-secondary sh_btnsecondary mr-2"
            clickHandler={onClose}
            label="Cancel"
          />
          <Buttons
            className="btn btn-primary sh_btnprimary"
            clickHandler={handleSave}
            label={isEditMode ? "Update" : "Save"}
            acl={isEditMode ? PERMISSIONS.VISITS.EDIT : PERMISSIONS.VISITS.CREATE}
          />
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default AddPrescriptionModal;
